'use client';

import type { Dispatch, SetStateAction } from 'react';
import { CountryScopeControl } from '@/new_components/shared/CountryScopeControl';
import type { i18n as I18n, TFunction } from 'i18next';
import { MapPin } from 'lucide-react';
import { countryScopeOptions } from '@/lib/countries';
import { FilterGroup } from '../FilterGroup';
import type { StagedFilters, StagedFilterSetters } from '../useSidebarFilterState';
import { BundeslandField, CityField } from './LocationFilterGroupParts';

export interface LocationFilterGroupProps {
  t: TFunction<'translation', undefined>;
  locationCount: number;
  clearLocation: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | null;
  lockOf: (control: string) => 'user' | null;
  cityOpen: boolean;
  setCityOpen: Dispatch<SetStateAction<boolean>>;
  setCitySearch: Dispatch<SetStateAction<string>>;
  /** The staged filter values and their setters (useSidebarFilterValues). */
  staged: StagedFilters;
  set: StagedFilterSetters;
  removeSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  citySearch: string;
  cityList: string[];
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  facetCount: (facet: string, value: string) => number | undefined;
  onCountryChange: ((country: string) => void) | undefined;
  bundeslandOpen: boolean;
  setBundeslandOpen: Dispatch<SetStateAction<boolean>>;
  i18n: I18n;
  locationOptions: { value: string; label: string }[];
}

export function LocationFilterGroup(props: LocationFilterGroupProps) {
  const {
    t,
    locationCount,
    clearLocation,
    openFilterGroup,
    handleGroupOpenChange,
    groupLock,
    onCountryChange,
    staged,
  } = props;
  return (
    <FilterGroup
      id="location"
      icon={MapPin}
      label={t('search.sidebar.groups.location', { defaultValue: 'Location' })}
      activeCount={locationCount}
      onClear={clearLocation}
      open={openFilterGroup === 'location'}
      onOpenChange={handleGroupOpenChange('location')}
      lock={groupLock('location')}
    >
      <CityField {...props} />

      {/* Country, right above the Bundesland list it narrows. Applied at
          once (SearchPageClient's applyCountry), like the old header flag;
          every country is the default. */}
      {onCountryChange && (
        <CountryScopeControl
          value={staged.country}
          options={['', ...countryScopeOptions('searchScope')]}
          onChange={onCountryChange}
        />
      )}

      <BundeslandField {...props} />
      {/* A new location filter: a field component in
          LocationFilterGroupParts.tsx reading staged.<key> / set.<key>,
          rendered here in sidebar order. */}
    </FilterGroup>
  );
}
